import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { apiClient } from '@/App';
import { Mail, Phone, Clock, MapPin, Send, Loader2, Facebook, MessageCircle } from 'lucide-react';

const Contact = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('Vă rugăm să completați toate câmpurile obligatorii');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Adresă de email invalidă');
      return;
    }

    setLoading(true);
    try {
      await apiClient.post('/contact', formData);
      toast.success('Mesajul a fost trimis cu succes! Vă vom contacta în curând.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Eroare la trimiterea mesajului. Încercați din nou.');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email',
      value: 'office@andeni-inmatriculari.ro',
      href: 'mailto:office@andeni-inmatriculari.ro'
    },
    {
      icon: Phone,
      title: 'Telefon',
      value: '0743 121 909',
      href: 'tel:+40743121909'
    },
    {
      icon: MessageCircle,
      title: 'WhatsApp',
      value: '0743 121 909',
      href: 'https://wa.me/40743121909'
    },
    {
      icon: Clock,
      title: 'Program',
      value: 'Luni - Vineri: 09:00 - 18:00',
      href: null
    },
    {
      icon: MapPin,
      title: 'Locație',
      value: 'România',
      href: null
    },
    {
      icon: Facebook,
      title: 'Facebook',
      value: 'Andeni Time Solutions',
      href: 'https://www.facebook.com/profile.php?id=61590294917178',
      external: true
    }
  ];

  return (
    <main className="min-h-screen bg-slate-50">
      {/* Header */}
      <section className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="max-w-2xl">
            <h1 className="font-display text-4xl md:text-5xl font-bold text-slate-900 mb-6" data-testid="contact-title">
              Contact
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Aveți întrebări? Suntem aici să vă ajutăm. Completați formularul 
              sau contactați-ne direct prin metodele de mai jos.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="text-xl font-semibold text-slate-900 mb-6">
                Informații de contact
              </h2>
              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <div 
                    key={info.title} 
                    className="flex items-start gap-4"
                    data-testid={`contact-info-${index}`}
                  >
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <info.icon className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 mb-1">{info.title}</p>
                      {info.href ? (
                        <a 
                          href={info.href}
                          className="text-slate-900 hover:text-blue-600 transition-colors"
                          {...(info.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                        >
                          {info.value}
                        </a>
                      ) : (
                        <p className="text-slate-900">{info.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Additional Info Card */}
              <Card className="mt-8 bg-blue-50 border-blue-100">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-blue-900 mb-2">
                    Răspuns rapid garantat
                  </h3>
                  <p className="text-sm text-blue-700">
                    Răspundem la toate mesajele în maxim 24 de ore lucrătoare. 
                    Pentru urgențe, vă recomandăm să ne contactați telefonic.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="bg-white border-slate-200 shadow-lg">
                <CardContent className="p-8">
                  <h2 className="text-xl font-semibold text-slate-900 mb-6">
                    Trimite-ne un mesaj
                  </h2>
                  
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name" className="text-slate-700 mb-2 block">
                          Nume *
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Numele dumneavoastră"
                          className="h-12"
                          data-testid="contact-input-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email" className="text-slate-700 mb-2 block">
                          Email *
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="email@exemplu.ro"
                          className="h-12"
                          data-testid="contact-input-email"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="phone" className="text-slate-700 mb-2 block">
                          Telefon
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="0712 345 678"
                          className="h-12"
                          data-testid="contact-input-phone"
                        />
                      </div>
                      <div>
                        <Label htmlFor="subject" className="text-slate-700 mb-2 block">
                          Subiect *
                        </Label>
                        <Input
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          placeholder="Despre ce doriți să discutăm?"
                          className="h-12"
                          data-testid="contact-input-subject"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-slate-700 mb-2 block">
                        Mesaj *
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Scrieți mesajul dumneavoastră aici..."
                        rows={6}
                        className="resize-none"
                        data-testid="contact-input-message"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={loading}
                      className="bg-blue-600 hover:bg-blue-700 text-white gap-2 w-full md:w-auto"
                      data-testid="contact-submit-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Se trimite...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4" />
                          Trimite mesajul
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
